import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { ArrowUpRight, Bus, ChevronDown, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import {
  WHATSAPP_BOOKING_CONTACT,
  WHATSAPP_DISPATCH_CONTACTS,
  WHATSAPP_DISPATCH_HEADING,
} from '../config/contact';
import { ROUTE_CONFIGS } from '../seo/routes';

const WhatsAppIcon = ({ className = 'h-5 w-5 fill-white sm:h-6 sm:w-6' }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

function isBusBookingPath(pathname) {
  if (pathname === '/bus') return true;
  const slug = pathname.replace(/^\//, '');
  const route = ROUTE_CONFIGS.find((r) => r.slug === slug);
  return route?.kind === 'bus';
}

const Avatar = ({ children }) => (
  <span
    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#128C7E]/15 text-[#075E54]"
    aria-hidden="true"
  >
    {children}
  </span>
);

const DISPATCH_MARKS = {
  Nairobi: '🇰🇪',
  Arusha: '🇹🇿',
};

const ContactRow = ({ href, name, hint, onClick, linkRef, ariaLabel, leading }) => (
  <a
    ref={linkRef}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={ariaLabel}
    className="flex items-center gap-2 rounded-lg border-y border-r border-stone-100 border-l-[3px] border-l-[#128C7E] bg-white py-2 pl-2 pr-2.5 shadow-sm ring-1 ring-black/5 hover:bg-stone-50 focus:outline-none focus:bg-stone-50"
    onClick={onClick}
  >
    {leading}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-xs font-medium text-stone-700">{name}</span>
      {hint ? <span className="block truncate text-[10px] leading-tight text-stone-400">{hint}</span> : null}
    </span>
    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden="true" />
  </a>
);

const WhatsAppAssistButton = () => {
  const location = useLocation();
  const liftFab = isBusBookingPath(location.pathname);
  const [open, setOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const firstItemRef = useRef(null);
  const panelId = useId();
  const dispatchPanelId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setDispatchOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    if (firstItemRef.current) {
      firstItemRef.current.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') close();
    };
    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open, close]);

  const bottomClass = liftFab
    ? 'bottom-[calc(8.75rem+env(safe-area-inset-bottom))] sm:bottom-[calc(8.75rem+env(safe-area-inset-bottom))] lg:bottom-6'
    : 'bottom-[calc(4.25rem+env(safe-area-inset-bottom))] sm:bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:bottom-6';

  return (
    <div
      ref={containerRef}
      className={`fixed z-[45] right-3 sm:right-4 ${bottomClass}`}
    >
      {open && (
        <div
          id={panelId}
          className="absolute bottom-full right-0 mb-3 w-60 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/10"
        >
          <div className="flex items-center gap-2 bg-gradient-to-r from-[#075E54] to-[#128C7E] px-3 py-2">
            <WhatsAppIcon className="h-4 w-4 fill-white" />
            <p className="text-xs font-semibold text-white">Start a conversation</p>
          </div>
          <ul className="space-y-1.5 p-2" aria-label="WhatsApp contacts">
            <li>
              <ContactRow
                linkRef={firstItemRef}
                href={`https://wa.me/${WHATSAPP_BOOKING_CONTACT.e164}`}
                name={WHATSAPP_BOOKING_CONTACT.label}
                hint={WHATSAPP_BOOKING_CONTACT.hint}
                ariaLabel={`WhatsApp ${WHATSAPP_BOOKING_CONTACT.label}, ${WHATSAPP_BOOKING_CONTACT.hint}`}
                leading={(
                  <Avatar>
                    <span className="text-[11px] font-semibold">A</span>
                  </Avatar>
                )}
                onClick={close}
              />
            </li>
            <li>
              <button
                type="button"
                aria-expanded={dispatchOpen}
                aria-controls={dispatchPanelId}
                onClick={() => setDispatchOpen((prev) => !prev)}
                className="flex w-full items-center gap-2 rounded-lg border-y border-r border-stone-100 border-l-[3px] border-l-[#128C7E] bg-white py-2 pl-2 pr-2.5 text-left shadow-sm ring-1 ring-black/5 hover:bg-stone-50 focus:outline-none focus:bg-stone-50"
              >
                <Avatar>
                  <Bus className="h-3.5 w-3.5" strokeWidth={2} />
                </Avatar>
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-stone-700">
                  {WHATSAPP_DISPATCH_HEADING}
                </span>
                <ChevronDown
                  aria-hidden="true"
                  className={`h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform ${
                    dispatchOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {dispatchOpen && (
                <ul id={dispatchPanelId} className="mt-1.5 space-y-1.5 pl-3" aria-label={WHATSAPP_DISPATCH_HEADING}>
                  {WHATSAPP_DISPATCH_CONTACTS.map((contact) => (
                    <li key={contact.e164}>
                      <ContactRow
                        href={`https://wa.me/${contact.e164}`}
                        name={contact.label}
                        ariaLabel={`WhatsApp ${contact.label}`}
                        leading={(
                          <Avatar>
                            <span className="text-sm leading-none">{DISPATCH_MARKS[contact.label]}</span>
                          </Avatar>
                        )}
                        onClick={close}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          </ul>
        </div>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setDispatchOpen(false);
          setOpen((prev) => !prev);
        }}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={panelId}
        aria-label={open ? 'Close WhatsApp chat' : 'Contact us on WhatsApp'}
        className="group flex items-center gap-1.5 sm:gap-2 transition-transform hover:scale-[1.02] active:scale-95"
      >
        {!open && (
          <span className="hidden sm:inline rounded-lg bg-[#f4f4f4] px-3 py-2 text-xs font-bold text-[#1f1f1f] shadow-md ring-1 ring-black/5 sm:px-4 sm:py-2.5 sm:text-sm">
            Let's Chat
          </span>
        )}
        <span className="flex h-11 w-11 sm:h-12 sm:w-12 md:h-14 md:w-14 shrink-0 items-center justify-center rounded-full bg-[#25D366] shadow-md sm:shadow-lg ring-1 ring-black/10 transition-colors group-hover:bg-[#1da851]">
          {open ? (
            <X className="h-5 w-5 text-white sm:h-6 sm:w-6" strokeWidth={2.5} aria-hidden="true" />
          ) : (
            <WhatsAppIcon />
          )}
        </span>
      </button>
    </div>
  );
};

export default WhatsAppAssistButton;
